import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  styled,
  Collapse,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Business as ProjectsIcon,
  Settings as SettingsIcon,
  ContactPhone as LeadsIcon,
  Assignment as TasksIcon,
  Call as CallsIcon,
  Home as SiteVisitsIcon,
  Assessment as ReportsIcon,
  ExpandLess,
  ExpandMore,
  Engineering as DeveloperIcon,
  Inventory2 as InventoryIcon,
  Receipt as BookingIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  BusinessCenter as ChannelPartnersIcon,
  WhatsApp as WhatsAppIcon,
  Extension as IntegrationsIcon,
  Bolt as SocialAutomationIcon,
} from '@mui/icons-material';
import PlayStoreBtn from '../common/PlayStoreBtn';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { fetchIntegrationsOverview } from '../../services/common';
import { LEAD_THEME as T } from '../../utils/lead_helper_v2';

// Responsive width values
const getDrawerWidth = (theme) => ({
  xs: '180px',
  sm: '190px',
  md: '200px',
  lg: '210px',
  xl: '220px',
});

// Further increase the collapsed width values
const getCollapsedWidth = (theme) => ({
  xs: '56px',    // Reduced from 64px
  sm: '60px',    // Reduced from 68px
  md: '64px',    // Reduced from 72px
  lg: '68px',    // Reduced from 76px
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

const getMenuItems = (user, whatsappConnected = false) => {
  if (!user) return [];
  
  // Check if user has permissions from role_permissions object
  const hasPermission = (feature) => {
    return user?.role_permissions?.[feature] === true;
  };
  
  // Base items available based on permissions
  const items = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: '/dashboard' 
    },
    // { 
    //   text: 'Leads Old', 
    //   icon: <LeadsIcon />, 
    //   path: '/leads' 
    // },
    { 
      text: 'Leads', 
      icon: <LeadsIcon />, 
      path: '/leads' 
    },
    { 
      text: 'Calls', 
      icon: <CallsIcon />, 
      path: '/calls' 
    },
  ];

  // Add WhatsApp menu item if connected
  if (whatsappConnected) {
    items.push({
      text: 'WhatsApp',
      icon: <WhatsAppIcon />,
      path: '/whatsapp'
    });
  }

  items.push(
    { 
      text: 'Site Visits', 
      icon: <SiteVisitsIcon />, 
      path: '/site-visits' 
    },
    { 
      text: 'Tasks & Calendar',
      icon: <TasksIcon />,
      path: '/calendar'
    },
    {
      text: 'Bookings',
      icon: <BookingIcon />,
      path: '/bookings'
    },
  );
  
  // Add Properties menu item only if user has permission
  // if (hasPermission('properties')) {
  //   items.push({ 
  //     text: 'Properties',
  //     icon: <ProjectsIcon />,
  //     subItems: [
  //       { 
  //         text: 'Developers',
  //         path: '/developers',
  //         icon: <DeveloperIcon />
  //        },
  //       {
  //         text: 'Projects',
  //         path: '/projects',
  //         icon: <ProjectsIcon />
  //       }
  //     ]
  //   });
  // }

  // Add Inventory menu (Projects, Developer)
  if (hasPermission('properties')) {
    items.push({
      text: 'Inventory',
      icon: <InventoryIcon />,
      subItems: [
        { text: 'Developer', path: '/inventory/developers', icon: <DeveloperIcon /> },
        { text: 'Projects', path: '/inventory/projects', icon: <ProjectsIcon /> },
      ]
    });
  }

  // Add Channel Partners if user has permission
  if (hasPermission('channel_partners')) {
    items.push({ 
      text: 'Channel Partners', 
      icon: <ChannelPartnersIcon />, 
      path: '/channel-partners' 
    });
  }
  // Reports (Reports v2) is the default entry in the left navigation.
  // Legacy /reports/* entries are intentionally hidden.
  items.push({
    text: 'Reports',
    icon: <ReportsIcon />,
    subItems: [
      { text: 'Executive Dashboard', path: '/reports-v2/executive-dashboard' },
      { text: 'Agent Intelligence', path: '/reports-v2/agent-intelligence' },
      { text: 'Conversation Intelligence', path: '/reports-v2/conversation-intelligence' },
      { text: 'Lead & Pipeline Intelligence', path: '/reports-v2/lead-pipeline' },
      { text: 'Source & Channel Performance', path: '/reports-v2/source-channel' },
      { text: 'Site Visit Intelligence', path: '/reports-v2/site-visit-intelligence' },
    ]
  })
  // Integrations — admin only (Facebook, WhatsApp, webhooks, etc.)
  if (user?.role === 'AD') {
    items.push({
      text: 'Integrations',
      icon: <IntegrationsIcon />,
      path: '/integrations',
    });
    items.push({
      text: 'Social Media Automation',
      icon: <SocialAutomationIcon />,
      path: '/social-media-automation',
    });
  }
  // Add Settings only if user has settings permission (Admin only)
  if (hasPermission('settings')) {
    items.push({ 
      text: 'Settings', 
      icon: <SettingsIcon />, 
      path: '/settings' 
    });
  }

  return items;
};

// Match LeadSidebarV2: green theme for selected state (left border, background, text)
const TEXT_COLOR = T.ink2;
const SELECTED_COLOR = T.green;
const SELECTED_BG = T.greenSf;

const Sidebar = ({ user, logout }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [expandedMenu, setExpandedMenu] = useState('');
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Add responsive detection
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Check WhatsApp connection status
  useEffect(() => {
    const checkIntegrations = async () => {
      try {
        const data = await fetchIntegrationsOverview();
        const whatsappIntegration = data.find(integration => integration.id === 'whatsapp');
        setWhatsappConnected(whatsappIntegration?.connected || false);
      } catch (error) {
        console.error('Error checking integrations:', error);
        setWhatsappConnected(false);
      }
    };

    if (user) {
      checkIntegrations();
    }
  }, [user]);

  // Auto collapse on small screens or leads page
  useEffect(() => {
    if (isSmallScreen) {
      setOpen(false);
    }
    // Remove the auto-collapse for leads and calls pages
  }, [isSmallScreen]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleSubMenuToggle = (menuName) => {
    setExpandedMenu(current => current === menuName ? '' : menuName);
  };

  const isSelected = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Helper function to check if item or its subitems are selected
  const isItemSelected = (item) => {
    if (isSelected(item.path)) return true;
    if (item.subItems) {
      return item.subItems.some(subItem => isSelected(subItem.path));
    }
    return false;
  };

  // Get menu items based on user permissions and WhatsApp connection
  const menuItems = getMenuItems(user, whatsappConnected);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: {
          xs: open ? getDrawerWidth(theme).xs : getCollapsedWidth(theme).xs,
          sm: open ? getDrawerWidth(theme).sm : getCollapsedWidth(theme).sm,
          md: open ? getDrawerWidth(theme).md : getCollapsedWidth(theme).md,
          lg: open ? getDrawerWidth(theme).lg : getCollapsedWidth(theme).lg,
          xl: open ? getDrawerWidth(theme).xl : getCollapsedWidth(theme).lg,
        },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: {
            xs: open ? getDrawerWidth(theme).xs : getCollapsedWidth(theme).xs,
            sm: open ? getDrawerWidth(theme).sm : getCollapsedWidth(theme).sm,
            md: open ? getDrawerWidth(theme).md : getCollapsedWidth(theme).md,
            lg: open ? getDrawerWidth(theme).lg : getCollapsedWidth(theme).lg,
            xl: open ? getDrawerWidth(theme).xl : getCollapsedWidth(theme).lg,
          },
          boxSizing: 'border-box',
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid',
          borderColor: T.border,
          bgcolor: '#fff',
          borderRadius: 0,
        },
      }}
    >
      <DrawerHeader
        sx={{
          height: '64px',  // Match exactly with Header height
          minHeight: '64px', // Force consistent height
          px: {
            xs: 1.5,
            sm: 1.75,
            md: 2,
          },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxSizing: 'border-box', // Ensure border is included in height
          '& .MuiIconButton-root': {  // Target the IconButton specifically
            height: 32,              // Fixed height for the button
            width: 32,               // Fixed width for the button
          }
        }}
      >
        {open ? (
          <Typography 
            variant="subtitle1" 
            sx={{ 
              ml: { xs: 0.5, sm: 1 },
              fontSize: {
                xs: '0.85rem',
                sm: '0.9rem',
                md: '1rem',
              },
              fontWeight: 600,
              whiteSpace: 'nowrap',
              lineHeight: 1,  // Adjust line height
            }}
          >
            SalesGro
          </Typography>
        ) : (
          <Typography 
            variant="subtitle2" 
            sx={{ 
              ml: { xs: 1, sm: 1.25 },
              fontWeight: 600,
              lineHeight: 1,  // Adjust line height
            }}
          >
            SG
          </Typography>
        )}
        <IconButton 
          onClick={handleDrawerToggle}
          size="small"
          sx={{ 
            p: { xs: 0.5, sm: 0.75 },
            mr: { xs: 1.5, sm: 2 },
          }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </DrawerHeader>

      <List sx={{ 
        pt: { xs: 0.5, sm: 0.75 },
        pb: { xs: 0.25, sm: 0.5 },
        px: 0.5,
        flex: 1,
        overflowY: 'auto',
      }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem
              button
              onClick={() => {
                if (item.subItems) {
                  handleSubMenuToggle(item.text);
                } else if (item.path) {
                  // Special case: when already on /leads (including with stat_type),
                  // clicking Leads should hard-reload the page to reset state.
                  if (item.path === '/leads' && location.pathname === '/leads') {
                    window.location.href = '/leads';
                  } else {
                    navigate(item.path);
                  }
                  setExpandedMenu('');
                }
              }}
              selected={isItemSelected(item)}
              sx={{
                minHeight: { xs: 28, sm: 32, md: 36 },
                py: { xs: 0.25, sm: 0.5 },
                px: { xs: 0.75, sm: 1 },
                mx: 0.5,
                mb: 0.25,
                borderRadius: 8,
                position: 'relative',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: isItemSelected(item) ? 600 : 500,
                transition: 'background-color 0.2s ease, color 0.2s ease',
                // Selected state — same as LeadSidebarV2: green bg, 3px left border, green text/icon
                ...(isItemSelected(item) && {
                  bgcolor: `${SELECTED_BG} !important`,
                  color: `${SELECTED_COLOR} !important`,
                  borderLeft: `3px solid ${T.green}`,
                  '& .MuiListItemIcon-root': { color: `${SELECTED_COLOR} !important` },
                  '& .MuiTypography-root': { color: `${SELECTED_COLOR} !important`, fontWeight: '600 !important' },
                }),
                ...(!isItemSelected(item) && {
                  color: TEXT_COLOR,
                  borderLeft: '3px solid transparent',
                }),
                '&:hover': {
                  bgcolor: isItemSelected(item) ? 'rgba(10,107,62,0.12) !important' : `${SELECTED_BG} !important`,
                  color: isItemSelected(item) ? `${SELECTED_COLOR} !important` : `${T.green} !important`,
                  '& .MuiListItemIcon-root': {
                    color: isItemSelected(item) ? `${SELECTED_COLOR} !important` : `${T.green} !important`,
                  },
                  '& .MuiTypography-root': {
                    color: isItemSelected(item) ? `${SELECTED_COLOR} !important` : `${T.green} !important`,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: { xs: 32, sm: 36, md: 40 },
                  mr: open ? { xs: 0.75, sm: 1, md: 1.5 } : 'auto',
                  justifyContent: 'center',
                  color: isItemSelected(item) ? `${SELECTED_COLOR} !important` : 'inherit',
                  '& svg': {
                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && (
                <>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                      fontWeight: isItemSelected(item) ? 600 : 500,
                      color: isItemSelected(item) ? `${SELECTED_COLOR} !important` : 'inherit',
                    }}
                  />
                  {item.subItems && (
                    <Box sx={{ ml: 0.5, color: 'inherit' }}>
                      {expandedMenu === item.text ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  )}
                </>
              )}
            </ListItem>

            {item.subItems && open && (
              <Collapse in={expandedMenu === item.text} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 1, pr: 0.5, pb: 0.25 }}>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.text}
                      button
                      onClick={() => {
                        if (!subItem.disabled) navigate(subItem.path)
                      }}
                      selected={isSelected(subItem.path)}
                      disabled={subItem.disabled}
                      sx={{
                        pl: { xs: 3, sm: 4, md: 5 },
                        py: { xs: 0.25, sm: 0.5 },
                        pr: 2,
                        mx: 0.5,
                        mb: 0.25,
                        borderRadius: 8,
                        minHeight: { xs: 24, sm: 28, md: 32 },
                        position: 'relative',
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: subItem.disabled ? 500 : (isSelected(subItem.path) ? 600 : 500),
                        transition: 'background-color 0.2s ease, color 0.2s ease',
                        ...(subItem.disabled && {
                          color: '#A0A0A0',
                          backgroundColor: '#f5f5f5',
                        }),
                        ...(!subItem.disabled && isSelected(subItem.path) && {
                          bgcolor: `${SELECTED_BG} !important`,
                          color: `${SELECTED_COLOR} !important`,
                          borderLeft: `3px solid ${T.green}`,
                          '& .MuiTypography-root': { color: `${SELECTED_COLOR} !important`, fontWeight: '600 !important' },
                        }),
                        ...(!subItem.disabled && !isSelected(subItem.path) && {
                          color: TEXT_COLOR,
                          backgroundColor: 'transparent',
                          borderLeft: '3px solid transparent',
                        }),
                        '&:hover': subItem.disabled
                          ? { cursor: 'not-allowed', bgcolor: '#f5f5f5' }
                          : {
                              bgcolor: isSelected(subItem.path) ? 'rgba(10,107,62,0.12) !important' : `${SELECTED_BG} !important`,
                              color: isSelected(subItem.path) ? `${SELECTED_COLOR} !important` : `${T.green} !important`,
                              '& .MuiTypography-root': {
                                color: isSelected(subItem.path) ? `${SELECTED_COLOR} !important` : `${T.green} !important`,
                              },
                            },
                      }}
                    >
                      <ListItemText
                        primary={subItem.text + (subItem.disabled ? ' (Coming Soon)' : '')}
                        primaryTypographyProps={{
                          fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                          fontWeight: isSelected(subItem.path) ? 600 : 500,
                          color: subItem.disabled ? '#A0A0A0' : (isSelected(subItem.path) ? `${SELECTED_COLOR} !important` : 'inherit'),
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      {/* Play Store Button and Logout button at the bottom */}
      <Box sx={{
        width: '100%',
        p: { xs: 1.5, sm: 2 },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.5, sm: 2 },
        alignItems: 'center',
      }}>
        {/* Play Store Button */}
        {open && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            width: '100%',
            maxWidth: '140px',
            '& img': {
              height: '36px !important',
              width: '140px !important',
              objectFit: 'contain',
            }
          }}>
            <PlayStoreBtn size="small" />
          </Box>
        )}
        
        {/* Logout Button */}
        {open ? (
          <Button
            variant="clearBtnOutlined"
            color="primary"
            sx={{ 
              minWidth: 120, 
              fontWeight: 600,
              width: '100%',
              maxWidth: '140px',
            }}
            startIcon={<PowerSettingsNewIcon />}
            onClick={logout}
          >
            Logout
          </Button>
        ) : (
          <Tooltip title="Logout" placement="right">
            <IconButton onClick={logout} color="inherit">
              <PowerSettingsNewIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar; 